import { Loader2, PackageSearch, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useM3AssegnaUpload, useM3DocumentiPacchetti } from "@/hooks/useM3";
import { cn } from "@/lib/utils";
import type { DocumentiUploadResponse } from "@/types/api";

/**
 * Disambiguazione (SPEC §3.2): una sorgente (DDT/fattura) caricata non si è
 * agganciata da sola → «A quale pacchetto appartiene?». Lista candidati + ricerca
 * su tutti i pacchetti. Mai un percorso di rifiuto.
 */
export function DisambiguaDialog({
  upload,
  onClose,
  onAssegnato,
}: {
  upload: DocumentiUploadResponse;
  onClose: () => void;
  onAssegnato: (caricoId: string) => void;
}) {
  const [q, setQ] = useState("");
  const [scelto, setScelto] = useState<string | null>(null);
  const assegna = useM3AssegnaUpload();
  const { data, isFetching } = useM3DocumentiPacchetti("tutti", q.trim() || undefined);

  // Candidati suggeriti dal backend + risultati ricerca (deduplicati).
  const righe = useMemo(() => {
    const suggeriti = (upload.candidati ?? []).map((c) => ({
      carico_id: c.carico_id,
      titolo: c.cliente ?? c.riferimento ?? c.carico_id,
      sotto: [c.riferimento, c.stato].filter(Boolean).join(" · "),
      suggerito: true,
    }));
    const ids = new Set(suggeriti.map((s) => s.carico_id));
    const cercati = (data?.items ?? [])
      .filter((p) => !ids.has(p.carico_id))
      .map((p) => ({
        carico_id: p.carico_id,
        titolo: p.cliente_ragione_sociale,
        sotto: [p.numero_ndp ? `NDP ${p.numero_ndp}` : null, p.paese_iso]
          .filter(Boolean)
          .join(" · "),
        suggerito: false,
      }));
    return [...suggeriti, ...(q.trim() ? cercati : [])];
  }, [upload.candidati, data, q]);

  function conferma() {
    if (!scelto || !upload.file_id) return;
    assegna.mutate(
      { fileId: upload.file_id, caricoId: scelto },
      {
        onSuccess: (p) => {
          onAssegnato(p.carico_id);
          onClose();
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !assegna.isPending && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageSearch className="h-5 w-5 text-brand-700" />
            A quale pacchetto appartiene?
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Ho letto {upload.classificazione === "fattura" ? "la fattura" : "il DDT"} ma non
          riesco ad agganciarlo. Scegli il pacchetto giusto.
        </p>

        <div className="flex items-center gap-1.5 rounded-md border border-border bg-muted px-2 py-1.5">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca cliente, NDP, paese…"
            className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
          />
        </div>

        <div className="max-h-[300px] space-y-1.5 overflow-y-auto">
          {isFetching && q.trim() && (
            <div className="flex items-center gap-2 px-1 py-2 text-[12px] text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Cerco…
            </div>
          )}
          {righe.length === 0 ? (
            <div className="py-6 text-center text-[13px] text-muted-foreground">
              Nessun pacchetto. Se manca, carica prima la NDP per crearlo.
            </div>
          ) : (
            righe.map((r) => (
              <button
                key={r.carico_id}
                type="button"
                onClick={() => setScelto(r.carico_id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md border p-2.5 text-left",
                  scelto === r.carico_id
                    ? "border-brand-500 bg-brand-50"
                    : "border-border hover:border-brand-300 hover:bg-brand-50/40",
                )}
              >
                <div className="flex-1">
                  <div className="text-[13px] font-medium">{r.titolo}</div>
                  {r.sotto && (
                    <div className="text-[11px] text-muted-foreground">{r.sotto}</div>
                  )}
                </div>
                {r.suggerito && (
                  <span className="rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700">
                    suggerito
                  </span>
                )}
              </button>
            ))
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={assegna.isPending}>
            Annulla
          </Button>
          <Button onClick={conferma} disabled={!scelto || assegna.isPending}>
            {assegna.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Aggancia al pacchetto
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
